import axios from 'axios';
import { API_BASE } from './apiBase';

const API = `${API_BASE}/api`;

// NOTE: Global axios interceptors (cache-busting, no-cache headers, withCredentials)
// are set in AuthContext.js to ensure they run before any API call.

// Orders API
export const ordersApi = {
    getAll: (params) => axios.get(`${API}/orders`, params ? { params } : undefined),
    getOne: (id) => axios.get(`${API}/orders/${id}`),
    create: (data) => axios.post(`${API}/orders`, data),
    requestQuote: (orderData, comment) => axios.post(`${API}/orders/request-quote`, { order: orderData, comment: comment || null }),
    update: (id, data) => axios.patch(`${API}/orders/${id}`, data),
    delete: (id, confirmText) => axios.delete(`${API}/orders/${id}`, { 
        data: { confirm_text: confirmText } 
    }),
    // Customer-specific delete (draft orders only, no confirmation needed)
    customerDelete: (id) => axios.delete(`${API}/orders/${id}/customer`),
    // Artwork share links (external designer collaboration)
    listArtworkShareLinks: (id) => axios.get(`${API}/orders/${id}/artwork-share-links`),
    createArtworkShareLink: (id, data) => axios.post(`${API}/orders/${id}/artwork-share-links`, data),
    revokeArtworkShareLink: (id, linkId) => axios.delete(`${API}/orders/${id}/artwork-share-links/${linkId}`),
    getPurchases: (id) => axios.get(`${API}/orders/${id}/purchases`),
    getFiles: (id) => axios.get(`${API}/orders/${id}/files`),
    submit: (id) => axios.post(`${API}/orders/${id}/submit`),
    // Product Elements
    getProductElements: (id) => axios.get(`${API}/orders/${id}/product-elements`),
    createProductElement: (id, data) => axios.post(`${API}/orders/${id}/product-elements`, data),
    updateProductElement: (id, elementId, data) => axios.patch(`${API}/orders/${id}/product-elements/${elementId}`, data),
    deleteProductElement: (id, elementId) => axios.delete(`${API}/orders/${id}/product-elements/${elementId}`),
    // Repro
    getReproPdf: (id) => `${API}/orders/${id}/repro-pdf`,
    getPreviewToken: (id) => axios.post(`${API}/orders/${id}/preview-token`),
    sendRepro: (id, data = null) => axios.post(`${API}/orders/${id}/send-repro`, data),
    getReproLogs: (id) => axios.get(`${API}/orders/${id}/repro-logs`),
    // Supplier Confirmation
    // All supplier-confirmation endpoints accept an optional ``variant``
    // parameter — ``production`` (default) targets the main
    // manufacturing PO, ``test_press`` targets the parallel Test Press
    // workflow. The two are hermetically isolated at the DB layer so
    // TP dispatches can never overwrite production data.
    getSupplierConfirmation: (id, variant = 'production') =>
        axios.get(`${API}/orders/${id}/supplier-confirmation`, { params: { variant } }),
    createSupplierConfirmation: (id, data, variant = 'production') =>
        axios.post(`${API}/orders/${id}/supplier-confirmation`, data, { params: { variant } }),
    updateSupplierConfirmation: (id, data, variant = 'production') =>
        axios.patch(`${API}/orders/${id}/supplier-confirmation`, data, { params: { variant } }),
    uploadSupplierConfirmationPdf: (id, file, variant = 'production') => {
        const formData = new FormData();
        formData.append('file', file);
        return axios.post(`${API}/orders/${id}/supplier-confirmation/upload-pdf`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            params: { variant },
        });
    },
    approveSupplierConfirmation: (id, data, variant = 'production') =>
        axios.post(`${API}/orders/${id}/supplier-confirmation/approve`, data, { params: { variant } }),
    reviseSupplierConfirmation: (id, data, variant = 'production') =>
        axios.post(`${API}/orders/${id}/supplier-confirmation/revise`, data, { params: { variant } }),
    getSupplierConfirmationHistory: (id, variant = 'production') =>
        axios.get(`${API}/orders/${id}/supplier-confirmation/history`, { params: { variant } }),
    // Quote workflow (status Quote → Quoted → Submitted)
    sendQuote: (id, data) => axios.post(`${API}/orders/${id}/quote/send`, data || {}),
    approveQuote: (id, data) => axios.post(`${API}/orders/${id}/quote/approve`, data || {}),
    rejectQuote: (id, data) => axios.post(`${API}/orders/${id}/quote/reject`, data || {}),
    getQuoteHistory: (id) => axios.get(`${API}/orders/${id}/quote/history`),
    restoreQuote: (id) => axios.post(`${API}/orders/${id}/quote/restore`),
    // Test Press manual status (Not required / Ordered / Shipped / Received /
    // Approved / Rejected). Any user with order access can update — the
    // customer often knows the physical state of a test press before the
    // admin does. Backend enforces `_check_order_access`.
    // Accepts either a plain string (legacy call sites — backwards
    // compatible) OR an object ``{status, customer_comment, force}``
    // used by the Phase 2 customer approve/reject flow. The payload
    // is passed through verbatim so the server-side guards (reject
    // comment required, admin-only ``force``) remain the source of
    // truth.
    updateTestPressStatus: (id, statusOrPayload) =>
        axios.patch(
            `${API}/orders/${id}/test-press-status`,
            typeof statusOrPayload === 'string'
                ? { status: statusOrPayload }
                : statusOrPayload,
        ),
    updateTestPressExpectedArrival: (id, expected_arrival) =>
        axios.patch(`${API}/orders/${id}/test-press-expected-arrival`, { expected_arrival }),
    // Phase 3 — admin forwards the customer's TP rejection to Sony.
    // Advances the state machine from ``rejected`` → ``awaiting_feedback``
    // and triggers Sony + customer confirmation emails on the server.
    sendTestPressFeedbackToSony: (id, feedback_text) =>
        axios.post(`${API}/orders/${id}/test-press/feedback-to-sony`, { feedback_text }),
    // Read-only recipient preview for the dialog above — resolves To /
    // CC / Reply-To with the exact same routing logic the send uses,
    // without sending anything.
    previewTestPressFeedbackToSony: (id) =>
        axios.get(`${API}/orders/${id}/test-press/feedback-to-sony/preview`),
    // Phase 4 — admin applies Sony's response. ``branch`` is one of
    // 'new_tp_v2' | 'fix_in_plant' | 'new_master'. Locked by the
    // server once applied — same TP cycle can't be re-branched.
    applyTestPressSonyResponse: (id, branch, note = null) =>
        axios.post(`${API}/orders/${id}/test-press/sony-response`, { branch, note }),
    // Phase 4 / history — list previously-closed Test Press cycles
    // (V1, V2…). Admin sees full snapshot incl. supplier confirmation
    // and Sony fields; customer sees sanitised snapshot (no Sony /
    // LEOS / supplier wording).
    getTestPressHistory: (id) =>
        axios.get(`${API}/orders/${id}/test-press/history`),
    // Admin-only safety-valve to correct the Test Press status on
    // legacy orders where the physical cycle completed before the
    // state machine existed. Forces the transition via the service
    // (``TP_EVENT.MANUAL_ADMIN``), records the reason on the audit
    // row, and sends NO customer / Sony emails. See
    // ``POST /orders/{id}/test-press/admin-override``.
    adminOverrideTestPressStatus: (id, status, reason) =>
        axios.post(`${API}/orders/${id}/test-press/admin-override`, { status, reason }),
    // Purchase Order (Step 0)
    updatePurchaseOrder: (id, data, variant = 'production') =>
        axios.patch(`${API}/orders/${id}/supplier-confirmation/purchase-order`, data, { params: { variant } }),
    markPurchaseOrderSent: (id, variant = 'production') =>
        axios.post(`${API}/orders/${id}/supplier-confirmation/purchase-order/send`, null, { params: { variant } }),
    // Admin recovery for an accidental "Save & Send" on the PO —
    // atomically restores the workflow to its pre-send state (clears
    // ``po_sent_at`` + OC approval, reverts order status from
    // In Progress back to Submitted, walks the TP state machine back
    // to REQUIRED on the TP variant, clears the sidebar TP arrival).
    // Preserves the full pre-undo state on ``po_undo_history``.
    // Preconditions: PO must be currently marked as sent AND no
    // dispatch has been approved yet.
    undoPurchaseOrderSent: (id, variant = 'production', body = {}) =>
        axios.post(`${API}/orders/${id}/supplier-confirmation/purchase-order/undo`, body, { params: { variant } }),
    resolveSupplierPartnerPreflight: (id, variant = 'production') =>
        axios.get(`${API}/orders/${id}/supplier-confirmation/resolve-partner`, { params: { variant } }),
    uploadPOPdf: (id, formData, variant = 'production') =>
        axios.post(`${API}/orders/${id}/supplier-confirmation/purchase-order/pdf`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            params: { variant },
        }),
    // Dispatch (Step 2)
    updateDispatch: (id, data, variant = 'production') =>
        axios.patch(`${API}/orders/${id}/supplier-confirmation/dispatch`, data, { params: { variant } }),
    updateDispatchDestination: (id, destinationId, data, variant = 'production') =>
        axios.patch(`${API}/orders/${id}/supplier-confirmation/dispatch/destinations/${destinationId}`, data, { params: { variant } }),
    approveDispatch: (id, variant = 'production') =>
        axios.post(`${API}/orders/${id}/supplier-confirmation/dispatch/approve`, null, { params: { variant } }),
    undoDispatch: (id, variant = 'production', body = {}) =>
        axios.post(`${API}/orders/${id}/supplier-confirmation/dispatch/undo`, body, { params: { variant } }),
    uploadDispatchPdf: (id, file, variant = 'production') => {
        const formData = new FormData();
        formData.append('file', file);
        return axios.post(`${API}/orders/${id}/supplier-confirmation/dispatch/upload-pdf`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            params: { variant },
        });
    },
    // Delivery Notice (Step 3 - now merged into Dispatch)
    sendDeliveryNotice: (id, data, variant = 'production') =>
        axios.post(`${API}/orders/${id}/supplier-confirmation/delivery-notice`, data, { params: { variant } }),
    // Supplier Events Audit Log
    getSupplierEvents: (id, variant = 'production') =>
        axios.get(`${API}/orders/${id}/supplier-events`, { params: { variant } }),
    // PO Lines (Production Cost)
    getPOLines: (id) => axios.get(`${API}/orders/${id}/po-lines`),
    // NCB Clearance
    updateNCBClearance: (id, data) => axios.patch(`${API}/orders/${id}/ncb-clearance`, data),
    getNCBAuditLogs: (id) => axios.get(`${API}/orders/${id}/ncb-audit`),
    // Admin manual quantity override (with optional propagation to PO + invoice lines)
    adminUpdateQuantity: (id, data) => axios.patch(`${API}/orders/${id}/admin-quantity`, data),
    // Master upload slots (manual extra slots for additional masters)
    addMasterSlot: (id, data) => axios.post(`${API}/orders/${id}/master-slots`, data),
    updateMasterSlot: (id, slotId, data) => axios.patch(`${API}/orders/${id}/master-slots/${slotId}`, data),
    deleteMasterSlot: (id, slotId) => axios.delete(`${API}/orders/${id}/master-slots/${slotId}`),
    // Admin master-file rejection workflow
    rejectMasterFile: (orderId, fileId, comment) =>
        axios.post(`${API}/admin/orders/${orderId}/files/${fileId}/reject`, { comment }),
    softDeleteMasterFile: (orderId, fileId, reason) =>
        axios.post(`${API}/admin/orders/${orderId}/files/${fileId}/soft-delete`, { reason: reason || 'duplicate' }),
    restoreMasterFile: (orderId, fileId) =>
        axios.post(`${API}/admin/orders/${orderId}/files/${fileId}/restore`),
    markMasterReplaced: (orderId, oldFileId, newFileId) =>
        axios.post(`${API}/orders/${orderId}/files/${oldFileId}/mark-replaced`, { new_file_id: newFileId }),
    // Tracklist validation (CD: DDP × booklet)
    getTracklistValidation: (id) => axios.get(`${API}/orders/${id}/tracklist-validation`),
    runTracklistValidation: (id) => axios.post(`${API}/orders/${id}/tracklist-validation/run`),
    acknowledgeTracklistValidation: (id, notes) => axios.post(`${API}/orders/${id}/tracklist-validation/acknowledge`, { notes: notes || null }),
    mapTracklistDisc: (id, discNumber, sectionIndex) => axios.post(`${API}/orders/${id}/tracklist-validation/map-disc`, {
        disc_number: discNumber,
        booklet_section_index: sectionIndex,
    }),
    // Per-disc approval — callable by customer or admin. Records who
    // signed off (name + role) plus optional notes for the audit trail.
    approveTracklistDisc: (id, discNumber, notes) => axios.post(
        `${API}/orders/${id}/tracklist-validation/approve-disc`,
        { disc_number: discNumber, notes: notes || null },
    ),
    revokeTracklistDiscApproval: (id, discNumber) => axios.post(
        `${API}/orders/${id}/tracklist-validation/revoke-disc-approval`,
        { disc_number: discNumber },
    ),
    // Sony Upload (Phase 1 + 2) — build/validate/persist the JSON job the
    // EC2 upload worker will consume. Admin only.
    previewSonyUpload: (id) => axios.get(`${API}/orders/${id}/sony-upload/preview`),
    createSonyUpload: (id) => axios.post(`${API}/orders/${id}/sony-upload`),
    getSonyUpload: (id) => axios.get(`${API}/orders/${id}/sony-upload`),
    retrySonyUpload: (id) => axios.post(`${API}/orders/${id}/sony-upload/retry`),
    // Admin only. Corrects FLOW's bookkeeping for a failed job whose
    // workflow_checkpoint is already ``upload_completed`` (Sony has
    // already confirmed the upload). Never retries, never starts the
    // EC2 worker / Chrome, never uploads to Sony again.
    reconcileSonyUploadCompleted: (jobId) =>
        axios.post(`${API}/admin/sony-upload/jobs/${jobId}/reconcile-completed`),
    // Admin recovery. Marks a FAILED or STALE Sony upload job as
    // completed because an operator finished the upload manually
    // outside FLOW (Aspera direct). The backend preserves the
    // failure history and sets completed_manually=true.
    completeSonyUploadManually: (jobId, note) =>
        axios.post(`${API}/admin/sony-upload/jobs/${jobId}/complete-manually`,
            { note: note || null }),
    // Admin recovery for the specific case where the Sony project was
    // MANUALLY DELETED after FLOW created it and the job then FAILED.
    // Rewinds workflow_checkpoint to not_started and requeues the SAME
    // job (preserving id / files / upload_units / operation_mode /
    // history). Requires explicit sony_project_deleted_confirmed.
    // Never claims the job, starts the worker, or uploads.
    resetSonyUploadAfterProjectDeletion: (jobId, note) =>
        axios.post(`${API}/admin/sony-upload/jobs/${jobId}/reset-after-project-deletion`,
            { sony_project_deleted_confirmed: true, note: note || null }),
    listSonyUploadJobs: (statusFilter) => axios.get(`${API}/admin/sony-upload/jobs`, {
        params: statusFilter ? { status: statusFilter } : {},
    }),
    dismissSonyUploadJob: (jobId, reason) =>
        axios.post(`${API}/admin/sony-upload/jobs/${jobId}/dismiss`, { reason: reason || null }),
    deleteSonyUploadJob: (jobId) =>
        axios.delete(`${API}/admin/sony-upload/jobs/${jobId}`),
};

// Files API
export const filesApi = {
    upload: (orderId, category, file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', category);
        return axios.post(`${API}/orders/${orderId}/files`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    // Smart download that handles both S3 (JSON response) and local (blob) files
    download: async (fileId) => {
        const response = await fetch(`${API}/files/${fileId}/download`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
            // S3 file - returns JSON with presigned URL
            const data = await response.json();
            if (data.download_url) {
                window.open(data.download_url, '_blank');
                return { data: null, isS3: true };
            }
            throw new Error('No download URL in response');
        } else if (response.ok) {
            // Local file - return blob for caller to handle
            const blob = await response.blob();
            return { data: blob, isS3: false };
        } else {
            throw new Error('Download failed');
        }
    },
    preview: (fileId) => axios.get(`${API}/files/${fileId}/preview`, { responseType: 'blob' }),
    // Inline-preview helper for PDFs (and other browser-renderable types).
    // Hits the new `/preview-url` endpoint which signs the URL with
    // `Content-Disposition: inline`, then opens it in a new tab.
    previewInline: async (fileId) => {
        const response = await fetch(`${API}/files/${fileId}/preview-url`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.detail || 'Preview failed');
        }
        const data = await response.json();
        if (!data.preview_url) throw new Error('No preview URL in response');
        window.open(data.preview_url, '_blank', 'noopener');
        return data;
    },
    delete: (fileId) => axios.delete(`${API}/files/${fileId}`),
    // Admin endpoint - get files with full S3 details
    getAdminFiles: (orderId) => axios.get(`${API}/admin/orders/${orderId}/files`),
    // Stalled chunked-upload sessions (status=uploading older than 10 min) so
    // the UI can show an "Upload interrupted" banner on Master Files card.
    getStalledUploads: (orderId) =>
        axios.get(`${API}/orders/${orderId}/stalled-uploads`),
    cancelStalledUpload: (orderId, sessionId) =>
        axios.delete(`${API}/orders/${orderId}/stalled-uploads/${sessionId}`),
    // Admin: re-run MD5/checksum verification for ALL masters + sidecars in
    // the order. Repairs orders uploaded before checksum verification
    // existed where files are present but master_status still shows Missing.
    reVerifyMasterFiles: (orderId) =>
        axios.post(`${API}/orders/${orderId}/master-files/re-verify`),
    // Order-level bulk: stream-download every master file in the order
    // that has no MD5 hash stored, compute the hashes server-side, then
    // re-run sidecar matching. Distinct from `reVerifyMasterFiles` which
    // is verification-only and skips files without a stored hash.
    generateMissingMd5: (orderId) =>
        axios.post(`${API}/orders/${orderId}/master-files/generate-missing-md5`),
    // Admin: FLOW-generated ``.md5`` sidecar reconciliation. Uses the
    // stored server-computed hash — never streams the master. Returns
    // the plan preview and the summary+sidecar list respectively.
    previewMd5SidecarPlan: (orderId) =>
        axios.get(`${API}/orders/${orderId}/master-files/md5-sidecar-plan`),
    generateMd5Sidecars: (orderId) =>
        axios.post(`${API}/orders/${orderId}/master-files/generate-md5-sidecars`),
    // Admin: stream-download a single file from S3 and recompute its MD5
    // hash server-side. Used when a master file was uploaded without a
    // client-side checksum so the matching sidecar entry stays pending.
    regenerateFileMd5: (fileId) =>
        axios.post(`${API}/files/${fileId}/regenerate-md5`),
    // Admin: verification-only re-run for a single file. Never touches
    // `md5_checksum` — only re-applies the sidecar-matching pass when
    // the file's `md5_verified` flag is stuck at None/pending.
    verifyFileMd5: (fileId) =>
        axios.post(`${API}/files/${fileId}/verify-md5`),
    // Automatic one-shot recovery: after a Master File upload batch
    // finishes the client fires this once per order. Backend regenerates
    // MD5 only for files still missing a hash, stamps
    // `md5_auto_retry_attempted` so it never loops, and leaves the
    // manual Generate MD5 action available if this second attempt also
    // fails.
    autoRetryMissingMd5: (orderId) =>
        axios.post(`${API}/orders/${orderId}/master-files/auto-retry-missing-md5`),
};

// Purchases API
export const purchasesApi = {
    getAll: () => axios.get(`${API}/purchases`),
    create: (data) => axios.post(`${API}/purchases`, data),
    update: (id, data) => axios.patch(`${API}/purchases/${id}`, data),
    delete: (id) => axios.delete(`${API}/purchases/${id}`),
};

// Metadata API
export const metadataApi = {
    getOrderStatuses: () => axios.get(`${API}/metadata/order-statuses`),
    getPurchaseTypes: () => axios.get(`${API}/metadata/purchase-types`),
    getPurchaseStatuses: () => axios.get(`${API}/metadata/purchase-statuses`),
    getFileCategories: () => axios.get(`${API}/metadata/file-categories`),
    getSuppliers: () => axios.get(`${API}/metadata/suppliers`),
    getSupplierConfirmationStatuses: () => axios.get(`${API}/metadata/supplier-confirmation-statuses`),
};

// Translations API
export const translationsApi = {
    getLanguages: () => axios.get(`${API}/translations/languages`),
    getStats: (lang) => axios.get(`${API}/translations/stats/${lang}`),
    getKeys: (lang = null, autoCapturedOnly = false) => axios.get(`${API}/translations/keys`, { 
        params: { lang, auto_captured_only: autoCapturedOnly || undefined } 
    }),
    createKey: (data) => axios.post(`${API}/translations/keys`, data),
    updateKey: (keyId, data) => axios.patch(`${API}/translations/keys/${keyId}`, data),
    deleteKey: (keyId) => axios.delete(`${API}/translations/keys/${keyId}`),
    setTranslation: (keyId, lang, text) => axios.put(`${API}/translations/keys/${keyId}/lang/${lang}`, { lang, text }),
    exportCsv: (lang) => `${API}/translations/export/${lang}`,
    importCsv: (lang, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return axios.post(`${API}/translations/import/${lang}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    getRuntime: (lang) => axios.get(`${API}/translations/runtime/${lang}`),
    createBulk: (keys) => axios.post(`${API}/translations/keys/bulk`, keys),
    reportMissing: (keys) => axios.post(`${API}/translations/missing`, { keys }),
    scanMojibake: () => axios.get(`${API}/translations/scan-mojibake`),
};

// Translation Center API — dynamic content (Visual Builder, Campaigns,
// Landing Pages, Bundles, Affinity Programmes) + unified CSV covering
// both static keys and dynamic content in a single file.
export const translationCenterApi = {
    getSources: () => axios.get(`${API}/translation-center/sources`),
    getEntries: (params) => axios.get(`${API}/translation-center/entries`, { params }),
    upsert: (data) => axios.put(`${API}/translation-center/entries`, data),
    exportUrl: (lang) => `${API}/translation-center/export/${lang}`,
    importCsv: (lang, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return axios.post(`${API}/translation-center/import/${lang}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

// Admin Users API
export const adminUsersApi = {
    getAll: () => axios.get(`${API}/auth/users`),
    getOne: (userId) => axios.get(`${API}/admin/users/${userId}`),
    create: (data) => axios.post(`${API}/admin/users`, data),
    update: (userId, data) => axios.put(`${API}/admin/users/${userId}`, data),
    getAuditLogs: (userId) => axios.get(`${API}/admin/users/${userId}/audit-logs`),
};

// Organizations API
export const organizationsApi = {
    getAll: () => axios.get(`${API}/organizations`),
    getOne: (orgId) => axios.get(`${API}/organizations/${orgId}`),
    update: (orgId, data) => axios.put(`${API}/admin/organizations/${orgId}`, data),
    getUsers: (orgId) => axios.get(`${API}/organizations/${orgId}/users`),
    getOrders: (orgId) => axios.get(`${API}/organizations/${orgId}/orders`),
    getBillingStatus: (orgId) => axios.get(`${API}/organizations/${orgId}/billing-status`),
    updateBillingProfile: (orgId, data) => axios.put(`${API}/organizations/${orgId}/billing-profile`, data),
    getPendingApproval: () => axios.get(`${API}/admin/organizations/pending-approval`),
    approveAndSync: (orgId) => axios.post(`${API}/admin/organizations/${orgId}/approve-and-sync`),
    // Attempt to auto-detect a company logo based on the website URL.
    // Always resolves — returns { logo_url: null } when nothing found.
    detectLogo: (orgId, website) => axios.post(`${API}/organizations/${orgId}/detect-logo`, { website }),
};

// Invoice Sync API (e-conomic integration)
export const invoiceSyncApi = {
    // Admin: trigger manual sync for an org
    triggerSync: (orgId) => axios.post(`${API}/invoice-sync/sync/${orgId}`),
    // Admin: trigger sync for ALL orgs
    triggerSyncAll: () => axios.post(`${API}/invoice-sync/sync-all`),
    // Get synced invoices (customer or admin)
    getInvoices: (orgId, status) => axios.get(`${API}/invoice-sync/invoices/${orgId}`, { params: status ? { status } : {} }),
    // Get credit status (customer or admin)
    getCreditStatus: (orgId) => axios.get(`${API}/invoice-sync/credit-status/${orgId}`),
    // Admin: set credit limit
    setCreditLimit: (orgId, creditLimit) => axios.patch(`${API}/invoice-sync/credit-limit/${orgId}`, { credit_limit: creditLimit }),
    // Admin: remove credit limit
    removeCreditLimit: (orgId) => axios.delete(`${API}/invoice-sync/credit-limit/${orgId}`),
    // Admin: get sync logs
    getSyncLogs: (orgId, limit) => axios.get(`${API}/invoice-sync/sync-logs/${orgId}`, { params: { limit: limit || 20 } }),
    // Admin: get finance overview across all orgs
    getFinanceOverview: () => axios.get(`${API}/invoice-sync/admin/finance-overview`),
    // Get invoice PDF URL
    getInvoicePdf: (orgId, invoiceNumber) => axios.get(`${API}/invoice-sync/invoice-pdf/${orgId}/${invoiceNumber}`, { responseType: 'blob' }),
};

// Partners API
export const partnersApi = {
    list: (params) => axios.get(`${API}/partners`, { params }),
    get: (id) => axios.get(`${API}/partners/${id}`),
    create: (data) => axios.post(`${API}/partners`, data),
    update: (id, data) => axios.put(`${API}/partners/${id}`, data),
    delete: (id) => axios.delete(`${API}/partners/${id}`),
    getDefaultRepro: () => axios.get(`${API}/partners/default-repro`),
    getTypes: () => axios.get(`${API}/metadata/partner-types`),
    getCurrencies: () => axios.get(`${API}/metadata/partner-currencies`),
};

// Artwork Jobs API (internal name: repro-jobs)
export const artworkJobsApi = {
    list: (params) => axios.get(`${API}/repro-jobs`, { params }),
    getMyJobs: () => axios.get(`${API}/repro-jobs/my-jobs`),
    get: (id) => axios.get(`${API}/repro-jobs/${id}`),
    create: (data) => axios.post(`${API}/repro-jobs`, data),
    assign: (id, partnerId, userId) => axios.patch(`${API}/repro-jobs/${id}/assign?partner_id=${partnerId}${userId ? `&user_id=${userId}` : ''}`),
    unassign: (id) => axios.patch(`${API}/repro-jobs/${id}/unassign`),
    resetToMissing: (id) => axios.post(`${API}/repro-jobs/${id}/reset-to-missing`),
    updateStatus: (id, status, comment) => axios.patch(`${API}/repro-jobs/${id}/status?status=${status}${comment ? `&comment=${encodeURIComponent(comment)}` : ''}`),
    completeJob: (id, note) => axios.post(`${API}/repro-jobs/${id}/complete`, { note: note || null }),
    upload: (id, formData) => axios.post(`${API}/repro-jobs/${id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    getOrderJob: (orderId) => axios.get(`${API}/orders/${orderId}/repro-job`),
    // Customer artwork upload (auto-creates job if needed)
    customerUpload: (orderId, formData, onUploadProgress) => axios.post(`${API}/orders/${orderId}/artwork-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress,
    }),
    // Register artwork files that were already uploaded via the chunked
    // pipeline (used for large files > 50 MB to bypass the ingress body
    // limit). Just takes the file_ids — backend reads metadata from
    // order_files and runs the same job/notification logic as the
    // multipart endpoint.
    registerChunkedArtwork: (orderId, fileIds) =>
        axios.post(`${API}/orders/${orderId}/artwork-upload/register`, { file_ids: fileIds }),
    // Customer-side artwork file deletion (only before repro has downloaded it)
    deleteCustomerArtworkFile: (orderId, fileId) => axios.delete(`${API}/orders/${orderId}/artwork-files/${fileId}`),
    // Customer artwork review (limited scope)
    getCustomerReview: (orderId) => axios.get(`${API}/orders/${orderId}/artwork-review`),
    submitCustomerReview: (orderId, action, comment) => axios.post(`${API}/orders/${orderId}/artwork-review`, { action, comment }),
    // Artwork versioning
    rejectArtwork: (jobId, comment) => axios.post(`${API}/repro-jobs/${jobId}/reject-artwork`, { comment }),
    approveArtwork: (jobId) => axios.post(`${API}/repro-jobs/${jobId}/approve-artwork`),
    getVersions: (jobId) => axios.get(`${API}/repro-jobs/${jobId}/versions`),
    // Per-file review
    approveFile: (jobId, fileId) => axios.post(`${API}/repro-jobs/${jobId}/files/${fileId}/approve`),
    rejectFile: (jobId, fileId, comment) => axios.post(`${API}/repro-jobs/${jobId}/files/${fileId}/reject`, { comment }),
    replaceFile: (jobId, fileId, formData) => axios.post(`${API}/repro-jobs/${jobId}/files/${fileId}/replace`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    // Per-file review for CORRECTED artwork (repro_files). Mirrors the
    // approve/reject/replace endpoints above but targets the partner-uploaded
    // V2 batch and lets the customer act on each file independently.
    approveCorrectedFile: (jobId, fileId) =>
        axios.post(`${API}/repro-jobs/${jobId}/repro-files/${fileId}/approve`),
    rejectCorrectedFile: (jobId, fileId, comment) =>
        axios.post(`${API}/repro-jobs/${jobId}/repro-files/${fileId}/reject`, { comment }),
    customerReplaceCorrectedFile: (jobId, fileId, formData) =>
        axios.post(`${API}/repro-jobs/${jobId}/repro-files/${fileId}/customer-replace`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
};

// Finance API
export const financeApi = {
    get: (orderId) => axios.get(`${API}/orders/${orderId}/finance`),
    getOrderFinance: (orderId) => axios.get(`${API}/orders/${orderId}/finance`),
    updatePurchase: (orderId, data) => axios.patch(`${API}/orders/${orderId}/finance/purchase`, data),
    bookPurchase: (orderId) => axios.post(`${API}/orders/${orderId}/finance/purchase/book`),
    updateInvoice: (orderId, data) => axios.patch(`${API}/orders/${orderId}/finance/invoice`, data),
    bookInvoice: (orderId) => axios.post(`${API}/orders/${orderId}/finance/invoice/book`),
    createInvoiceDraft: (orderId) => axios.post(`${API}/orders/${orderId}/finance/invoice/create`),
    getEvents: (orderId) => axios.get(`${API}/orders/${orderId}/finance/events`),
    
    // Unified Finance Summary (Phase 1-2)
    getFinanceSummary: (orderId) => axios.get(`${API}/orders/${orderId}/finance/summary`),
    recalculateProductionCost: (orderId) => axios.post(`${API}/orders/${orderId}/finance/recalculate-production-cost`),
    
    // Manual Supplier Costs (for CD/DVD/BD)
    getSupplierCosts: (orderId) => axios.get(`${API}/orders/${orderId}/finance/supplier-costs`),
    addSupplierCost: (orderId, data) => axios.post(`${API}/orders/${orderId}/finance/supplier-costs`, data),
    updateSupplierCost: (orderId, costId, data) => axios.patch(`${API}/orders/${orderId}/finance/supplier-costs/${costId}`, data),
    deleteSupplierCost: (orderId, costId) => axios.delete(`${API}/orders/${orderId}/finance/supplier-costs/${costId}`),
    
    // Sales Document Lifecycle
    getSalesDocument: (orderId) => axios.get(`${API}/orders/${orderId}/finance/sales-document`),
    transitionSalesDocument: (orderId, targetStatus) => axios.post(`${API}/orders/${orderId}/finance/sales-document/transition`, null, { params: { target_status: targetStatus } }),
    
    // Invoice Line Items (Invoice Basis / Order Breakdown)
    getInvoiceLines: (orderId) => axios.get(`${API}/orders/${orderId}/finance/invoice-lines`),
    addInvoiceLine: (orderId, data) => axios.post(`${API}/orders/${orderId}/finance/invoice-lines`, data),
    updateInvoiceLine: (orderId, lineId, data) => axios.patch(`${API}/orders/${orderId}/finance/invoice-lines/${lineId}`, data),
    deleteInvoiceLine: (orderId, lineId) => axios.delete(`${API}/orders/${orderId}/finance/invoice-lines/${lineId}`),
    reorderInvoiceLines: (orderId, lineIds) => axios.post(`${API}/orders/${orderId}/finance/invoice-lines/reorder`, { line_ids: lineIds }),
    autoPopulateInvoiceLines: (orderId) => axios.post(`${API}/orders/${orderId}/finance/invoice-lines/auto-populate`),
    
    // Invoice PDF
    downloadInvoicePdf: (orderId) => axios.get(`${API}/orders/${orderId}/invoice-pdf`, { responseType: 'blob' }),

    // Production Purchase PDF (email-to-supplier PO)
    downloadProductionPurchasePdf: (orderId) =>
        axios.get(`${API}/orders/${orderId}/production-purchase/pdf`, { responseType: 'blob' }),
    
    // E-conomic sync
    syncFromEconomic: (orderId) => axios.post(`${API}/orders/${orderId}/finance/invoice/sync-from-economic`),
    // Diagnostic — returns search-strategy results WITHOUT mutating state.
    // Used by the "Diagnose sync" admin action to show why auto-link failed.
    syncDiagnostics: (orderId) =>
        axios.get(`${API}/orders/${orderId}/finance/invoice/sync-diagnostics`),
    // READ-ONLY preview of an existing e-conomic booked invoice —
    // used by the "Link existing e-conomic invoice" recovery flow so
    // the admin can verify customer/amount/currency/date BEFORE
    // committing the link.
    previewEconomicInvoice: (orderId, bookedInvoiceNumber) =>
        axios.get(`${API}/orders/${orderId}/finance/invoice/preview-economic-invoice`, {
            params: { booked_invoice_number: bookedInvoiceNumber },
        }),

    // Manual recovery — link an OwnTune order to a booked e-conomic invoice
    // by number, used when auto-sync can't resolve the booked counterpart.
    // ``confirmReplace`` is required by the backend when the invoice is
    // already linked; the dialog re-submits with true after the user
    // confirms the replace.
    linkBookedInvoice: (orderId, bookedInvoiceNumber, confirmReplace = false) =>
        axios.post(`${API}/orders/${orderId}/finance/invoice/link-booked`, null, {
            params: {
                booked_invoice_number: bookedInvoiceNumber,
                confirm_replace: confirmReplace,
            },
        }),

    // Administrative recovery — sever the OwnTune ↔ e-conomic link on an
    // invoice WITHOUT touching e-conomic. Returns invoice to unlinked
    // ("local_draft") state so it can be linked again to the correct doc.
    unlinkFromEconomic: (orderId, reason) =>
        axios.post(`${API}/orders/${orderId}/finance/invoice/unlink-from-economic`, {
            confirm: true,
            reason: reason || null,
        }),
    
    // AI Insights
    getAiInsights: () => axios.get(`${API}/orders/ai-insights`),
};


// Catalogue API
export const catalogueApi = {
    getAll: (params) => axios.get(`${API}/catalogue`, { params }),
    getMetrics: () => axios.get(`${API}/catalogue/metrics`),
    getAiInsights: () => axios.get(`${API}/catalogue/ai-insights`),
    getOne: (id) => axios.get(`${API}/catalogue/${id}`),
    getDetail: (id) => axios.get(`${API}/catalogue/${id}/detail`),
    getByNumber: (catalogueNumber) => axios.get(`${API}/catalogue/by-number/${encodeURIComponent(catalogueNumber)}`),
    create: (data) => axios.post(`${API}/catalogue`, data),
    update: (id, data) => axios.put(`${API}/catalogue/${id}`, data),
    delete: (id) => axios.delete(`${API}/catalogue/${id}`),
    reorder: (id, quantity) => axios.post(`${API}/catalogue/${id}/reorder`, quantity != null ? { quantity } : {}),
    reorderQuote: (id, quantity) => axios.get(`${API}/catalogue/${id}/reorder-quote`, { params: { quantity } }),
    // Effective min-reorder policy for a catalogue entry (entry override → family default).
    reorderPolicy: (id) => axios.get(`${API}/catalogue/${id}/reorder-policy`),
    // Admin: family-level reorder minimums (vinyl / cd / dvd / bluray).
    listReorderPolicies: () => axios.get(`${API}/catalogue/reorder-policies`),
    updateReorderPolicies: (policies) => axios.put(`${API}/catalogue/reorder-policies`, { policies }),
    requestQuote: (id, { quantity, comment }) => axios.post(`${API}/catalogue/${id}/request-quote`, { quantity, comment }),
    import: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return axios.post(`${API}/catalogue/import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    downloadImportTemplate: () => axios.get(`${API}/catalogue/import-template`, { responseType: 'blob' }),
};


// Catalogue Integrations API
export const catalogueIntegrationsApi = {
    get: () => axios.get(`${API}/catalogue/integrations`),
    save: (data) => axios.post(`${API}/catalogue/integrations`, data),
    update: (data) => axios.put(`${API}/catalogue/integrations`, data),
    test: () => axios.post(`${API}/catalogue/integrations/test`),
    sync: () => axios.post(`${API}/catalogue/integrations/sync`),
    syncLogs: () => axios.get(`${API}/catalogue/integrations/sync-logs`),
    remove: () => axios.delete(`${API}/catalogue/integrations`),
    getDefaultMappings: (provider) => axios.get(`${API}/catalogue/integrations/default-mappings/${provider}`),
};

export const shopifyApi = {
    status: () => axios.get(`${API}/shopify/status`),
    oauthInit: (shop) => axios.post(`${API}/shopify/oauth/init`, { shop }),
    oauthCallback: (params) => axios.post(`${API}/shopify/oauth/callback`, params),
    sync: () => axios.post(`${API}/shopify/sync`),
    syncLogs: () => axios.get(`${API}/shopify/sync-logs`),
    disconnect: () => axios.post(`${API}/shopify/disconnect`),
};


// Upload Links API (for external partner uploads)
export const uploadLinksApi = {
    list: (orderId) => axios.get(`${API}/orders/${orderId}/upload-links`),
    create: (orderId, data) => axios.post(`${API}/orders/${orderId}/upload-links`, data),
    revoke: (orderId, linkId) => axios.delete(`${API}/orders/${orderId}/upload-links/${linkId}`),
    // Public endpoints (no auth)
    getPublicInfo: (token) => axios.get(`${API}/upload-links/${token}`),
    verifyPassword: (token, password) => axios.post(`${API}/upload-links/${token}/verify-password`, { password }),
};

// User Profile API (full profile + notification preferences)
export const userProfileApi = {
    getProfile: (userId) => axios.get(`${API}/users/${userId}/profile`),
    updateNotifications: (userId, companyId, preferences) =>
        axios.patch(`${API}/users/${userId}/notifications/${companyId}`, { preferences }),
    updateSelf: (data) => axios.patch(`${API}/auth/me`, data),
    sendPasswordReset: (userId) => axios.post(`${API}/users/${userId}/send-password-reset`),
};

// Auth-scoped helpers (session-user actions). Kept lightweight so
// consumers can import from a single place.
export const authApi = {
    // Set the signed-in user's profile picture. `dataUrl` must be a
    // JPG/PNG/WebP base64 data URI produced by the client-side
    // cropper. Backend caps size at ~700 KB.
    uploadProfilePicture: (dataUrl) =>
        axios.post(`${API}/auth/profile/picture`, { data_url: dataUrl }),
    // Clear the custom picture. If the user signed in with Google
    // their cached Google URL is restored automatically by the API.
    removeProfilePicture: () => axios.delete(`${API}/auth/profile/picture`),
};

// Company Team API (Phase 3 — customer self-management)
export const companyTeamApi = {
    getManageableCompanies: () => axios.get(`${API}/me/manageable-companies`),
    getTeam: (companyId) => axios.get(`${API}/companies/${companyId}/team`),
    invite: (companyId, payload) => axios.post(`${API}/companies/${companyId}/team/invite`, payload),
    updateRole: (companyId, userId, role) =>
        axios.patch(`${API}/companies/${companyId}/team/${userId}/role`, { role }),
    remove: (companyId, userId) =>
        axios.delete(`${API}/companies/${companyId}/team/${userId}`),
    updateNotifications: (companyId, userId, preferences) =>
        axios.patch(`${API}/companies/${companyId}/team/${userId}/notifications`, { preferences }),
};

// Memberships API (many-to-many user-company relationships)
export const membershipsApi = {
    // Get all companies a user belongs to
    getUserCompanies: (userId) => axios.get(`${API}/users/${userId}/companies`),
    // Get all users belonging to a company
    getCompanyUsers: (companyId) => axios.get(`${API}/organizations/${companyId}/users`),
    // Create a new membership
    create: (data) => axios.post(`${API}/memberships`, data),
    // Update a membership (role, is_primary)
    update: (membershipId, data) => axios.put(`${API}/memberships/${membershipId}`, data),
    // Remove a user from a company
    delete: (membershipId) => axios.delete(`${API}/memberships/${membershipId}`),
    // Bulk create memberships for a user
    bulkCreate: (userId, companyIds, role = 'member', primaryCompanyId = null) => 
        axios.post(`${API}/memberships/bulk`, null, { 
            params: { user_id: userId, company_ids: companyIds, role, primary_company_id: primaryCompanyId } 
        }),
    // Search users to add to a company
    searchUsersForCompany: (companyId, query = '', excludeMembers = true) => 
        axios.get(`${API}/memberships/search/users`, { 
            params: { company_id: companyId, q: query, exclude_members: excludeMembers } 
        }),
    // Search companies to add a user to
    searchCompaniesForUser: (userId, query = '', excludeMemberships = true) => 
        axios.get(`${API}/memberships/search/companies`, { 
            params: { user_id: userId, q: query, exclude_memberships: excludeMemberships } 
        }),
};

// Supplier Reconciliation (Finance — V1 MVP)
export const supplierReconciliationApi = {
    syncFromEconomic: (days = 90) =>
        axios.post(`${API}/supplier-reconciliation/sync`, null, { params: { days } }),
    listRows: (params = {}) =>
        axios.get(`${API}/supplier-reconciliation/rows`, { params }),
    searchInvoices: (q, onlyUnmatched = true) =>
        axios.get(`${API}/supplier-reconciliation/invoices/search`, {
            params: { q, only_unmatched: onlyUnmatched },
        }),
    match: (purchaseId, invoiceId, note) =>
        axios.post(`${API}/supplier-reconciliation/match`, {
            purchase_id: purchaseId,
            invoice_id: invoiceId,
            note,
        }),
    unmatch: (matchId) =>
        axios.delete(`${API}/supplier-reconciliation/match/${matchId}`),
    debugFetch: (days = 30) =>
        axios.get(`${API}/supplier-reconciliation/debug-fetch`, { params: { days } }),
    explain: (invoiceId) =>
        axios.get(`${API}/supplier-reconciliation/explain/${invoiceId}`),
    diagnostic: (supplierNumber = 1) =>
        axios.get(`${API}/supplier-reconciliation/diagnostic`, { params: { supplier_number: supplierNumber } }),
};


